import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useData } from '../contexts/DataContext';
import ComplianceBar from '../components/ComplianceBar';
import SchemaMarkup from '../components/SchemaMarkup';

const { FiShield, FiUsers, FiFileText, FiLock, FiCheckCircle, FiArrowRight, FiEye, FiUserCheck, FiAlertTriangle, FiBuilding, FiStar, FiVideo, FiMinus, FiPlus, FiHelpCircle, FiAnchor, FiBriefcase, FiAward, FiMail, FiPhone, FiSend, FiActivity, FiCheck, FiGlobe, FiMenu, FiSearch, FiBell, FiHome, FiSettings, FiPieChart, FiMessageSquare, FiClock } = FiIcons;

const ContentSection = ({ title, children, className = "bg-white", mode = "authoritative" }) => {
  const isAuth = mode === 'authoritative';
  const containerWidth = isAuth ? "max-w-5xl" : "max-w-3xl";
  const titleSize = "text-3xl md:text-4xl";
  const textSize = "text-lg md:text-[19px]";
  const leadingClass = isAuth ? "leading-loose" : "leading-relaxed";
  const spacingClass = isAuth ? "py-24 md:py-32" : "py-24 md:py-32";

  return (
    <section className={`${className} ${spacingClass}`}>
      <div className={`${containerWidth} mx-auto px-4 sm:px-6 lg:px-8`}>
        <h2 className={`${titleSize} font-bold text-[#0F172A] mb-8 text-left tracking-tight`}>
          {title}
        </h2>
        <div className={`${textSize} text-slate-600 ${leadingClass} space-y-6 text-left font-normal`}>
          {children}
        </div>
      </div>
    </section>
  );
};

const EnhancedLandingPage = () => {
  const navigate = useNavigate();
  const { subscriptionPlans = [], externalServices: contextExternalServices = [] } = useData();
  const [showFloatingBtn, setShowFloatingBtn] = useState(false);
  const [activeAccordion, setActiveAccordion] = useState(null);

  // --- FALLBACK DATA FOR PRICING IF CONTEXT IS EMPTY ---
  const defaultPlans = [
    { id: 'grunnpakke', name: 'Grunnpakke', price: 599, employees: '5-49 ansatte', features: ['Anonym varslingskanal', 'Enkel saksbehandling', 'Standard HMS-rapportering', 'E-post støtte'] },
    { id: 'standard', name: 'Standard (EU Direktiv)', price: 990, employees: '50-249 ansatte', features: ['Alle funksjoner i Grunnpakke', 'I samsvar med EUs varslingsdirektiv', 'Automatiserte frister', 'Sikker kommunikasjon', 'Prioritert støtte'], popular: true },
    { id: 'konsern', name: 'Konsern Pakke', price: 1990, employees: 'Ubegrenset', features: ['Alle funksjoner i Standard', 'Flere juridiske enheter', 'Konsern-dashboard', 'Dedikert kontaktperson', 'API-tilgang'] }
  ];

  const displayPlans = subscriptionPlans.length > 0 ? subscriptionPlans : defaultPlans;

  // --- FALLBACK FOR EXTERNAL SERVICES ---
  const defaultServices = [
    { id: 'external-single', name: 'Enkelt varsel (Ekstern)', price: 6900, timeframe: 'sak', description: 'Engangshåndtering av én sak av ekstern utreder.', category: 'external', active: true, icon: 'FiShield' },
    { id: 'external-partner', name: 'Varslingspartner', price: 1990, timeframe: 'mnd', description: 'Fast avtale om eksternt varslingsmottak.', category: 'external', active: true, icon: 'FiBriefcase' }
  ];

  const displayServices = contextExternalServices.length > 0 ? contextExternalServices : defaultServices;

  useEffect(() => {
    const handleScroll = () => {
      setShowFloatingBtn(window.scrollY > 400);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const toggleAccordion = (index) => {
    setActiveAccordion(activeAccordion === index ? null : index);
  };

  const handleContactClick = (serviceName) => {
    window.location.href = `mailto:support@hmsvarsling.no?subject=Forespørsel om ${encodeURIComponent(serviceName)}`;
  };

  const features = [
    { icon: FiShield, title: 'Anonym Varsling', description: 'Trygg kanal som skjermer varslerens identitet.', color: 'blue' },
    { icon: FiFileText, title: 'Saksbehandling', description: 'Strukturert prosess med tidsfrister og maler.', color: 'indigo' },
    { icon: FiAward, title: 'EU & AML Start', description: 'Oppfyller alle lovkrav for norske bedrifter.', color: 'purple' },
    { icon: FiLock, title: 'GDPR Sikkerhet', description: 'Innebygd personvern og tilgangskontroll.', color: 'teal' }
  ];

  const modules = [
    { icon: FiEye, title: 'Varsler-modul', description: 'Enkel inngang for ansatte via QR eller lenke.', path: '/whistleblower/login', theme: 'blue', headerBg: 'bg-blue-100', iconColor: 'text-blue-600', bodyBg: 'bg-[#F8FAFF]' },
    { icon: FiUsers, title: 'Bedriftsmodul', description: 'Komplett dashboard for saksbehandling.', path: '/company/login', theme: 'indigo', headerBg: 'bg-indigo-100', iconColor: 'text-indigo-600', bodyBg: 'bg-[#F8FAFF]' },
    { icon: FiUserCheck, title: 'Varslet person', description: 'Egen portal for innsyn og kontradiksjon.', path: '/reported/login', theme: 'purple', headerBg: 'bg-purple-100', iconColor: 'text-purple-600', bodyBg: 'bg-[#F8FAFF]' },
    { icon: FiAlertTriangle, title: 'HMS-hendelser', description: 'Rapporter ulykker og avvik i samme system.', path: '/hms/company-selection', theme: 'orange', headerBg: 'bg-orange-100', iconColor: 'text-orange-600', bodyBg: 'bg-[#FFF9F5]' },
    { icon: FiBriefcase, title: 'Utrederportal', description: 'Verktøy for eksterne granskere. Inkl. Microsite.', path: '/utreder/login', theme: 'teal', headerBg: 'bg-teal-100', iconColor: 'text-teal-600', bodyBg: 'bg-[#F0FDFA]', badge: 'Nyhet' }
  ];

  const faqItems = [
    { question: "Hva sier Arbeidsmiljøloven (AML) om 5 ansatte?", answer: "I henhold til Arbeidsmiljøloven § 2 A-6 plikter alle virksomheter med 5 eller flere ansatte å ha skriftlige rutiner for intern varsling. Også virksomheter med færre enn 5 ansatte skal ha slike rutiner dersom forholdene tilsier det." },
    { question: "Hva innebærer EUs varslingsdirektiv og regelen om 50 ansatte?", answer: "EUs varslingsdirektiv stiller strengere krav til bedrifter med 50 eller flere ansatte om å ha en sikker, intern varslingskanal, sikre konfidensialitet, og følge spesifikke tidsfrister." },
    { question: "Hva er '249-regelen' i varslingssammenheng?", answer: "Dette refererer til kravene i EUs varslingsdirektiv. Bedrifter med over 50 ansatte må ha interne varslingskanaler. Virksomheter med over 249 ansatte har enda strengere krav til rapportering, og vi anbefaler ofte vår 'Standard' eller 'Konsern' pakke for å sikre full etterlevelse." },
    { question: "Hva gjør plattformen konkret for oss?", answer: "Vi gir dere en ferdig, digital varslingskanal som oppfyller alle lovkrav. Dere får et mottakssenter for varsler, verktøy for saksbehandling, og ferdige maler for varslingsrutiner. Alt er klart til bruk på 5 minutter." },
    { question: "Hvordan fungerer abonnement og bindingstid?", answer: "Tjenesten er et løpende abonnement med månedlig eller årlig fakturering. Vi har ingen lang bindingstid, kun standard 3 måneders oppsigelsestid." },
    { question: "Er løsningen virkelig anonym?", answer: "Ja. Systemet er bygget for å skjerme varslerens identitet. All kommunikasjon er kryptert, og vi lagrer ingen IP-adresser eller metadata som kan spore varsleren, med mindre de selv velger å oppgi navn." },
    { question: "Kan vi bruke dette til generelt HMS-arbeid også?", answer: "Absolutt! Plattformen inkluderer moduler for å rapportere HMS-hendelser, ulykker, nestenulykker og kvalitetsavvik (RUH). Dere får full oversikt i samme dashboard." }
  ];

  const faqSchemaData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqItems.map(item => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": item.answer
      }
    }))
  };

  const topRowPlans = ['grunnpakke', 'standard', 'konsern'];
  const bottomRowPlans = ['offentlig-kommunal', 'idrettslag-frivillig'];

  const getPlan = (id) => displayPlans.find(p => p.id === id);

  const styles = {
    cardContainer: "rounded-[20px] shadow-[0px_8px_24px_rgba(15,23,42,0.08)] border-0 overflow-hidden h-full flex flex-col hover:shadow-[0px_12px_32px_rgba(15,23,42,0.12)] hover:-translate-y-1 transition-all duration-300 relative",
    buttonPrimary: "bg-[#1E40AF] hover:bg-[#1B3A9A] text-white font-semibold rounded-xl px-8 py-4 transition-all shadow-lg shadow-blue-900/10 flex items-center justify-center gap-2 text-[15px]",
    buttonSecondary: "bg-white border border-gray-200 text-[#0F172A] hover:bg-[#F8FAFF] font-semibold rounded-xl px-8 py-4 transition-colors shadow-sm flex items-center justify-center gap-2 text-[15px]",
    sectionPadding: "py-20 md:py-32",
    h1: "text-[40px] md:text-[56px] font-extrabold leading-[1.1] text-[#0F172A] tracking-tight",
    h2: "text-[32px] md:text-[40px] font-bold leading-[1.2] text-[#0F172A] tracking-tight",
    h3: "text-[20px] md:text-[24px] font-bold leading-[1.3] text-[#0F172A]",
    body: "text-[16px] md:text-[18px] font-normal leading-[1.6] text-[#475569]",
  };

  return (
    <div className="min-h-screen font-sans text-[#0F172A] bg-white overflow-x-hidden">
      <SchemaMarkup data={faqSchemaData} />
      <div className="min-h-screen">
        {/* --- HEADER --- */}
        <header className="bg-white/95 backdrop-blur-md shadow-sm sticky top-0 z-50 border-b border-gray-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
            <div className="flex items-center space-x-3 cursor-pointer group" onClick={() => navigate('/')}>
              <div className="w-9 h-9 bg-[#1E40AF] rounded-lg flex items-center justify-center shadow-sm">
                <SafeIcon icon={FiShield} className="text-white text-lg" />
              </div>
              <h1 className="text-lg font-bold tracking-tight text-[#0F172A]">HMSVarsling</h1>
            </div>
            
            <nav className="hidden md:flex items-center space-x-6 text-[15px] font-medium text-[#64748B]">
              <button onClick={() => navigate('/')} className="flex items-center gap-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full hover:bg-blue-200 transition-colors font-bold">
                <SafeIcon icon={FiIcons.FiHome} /> Hjem
              </button>
              <button onClick={() => navigate('/hvordan-fungerer-det')} className="hover:text-[#1E40AF]">Hvordan det fungerer</button>
              <button onClick={() => scrollToSection('pricing')} className="hover:text-[#1E40AF]">Priser</button>
              <button onClick={() => navigate('/regelverk-og-etterlevelse')} className="hover:text-[#1E40AF]">Regelverk</button>
              <button onClick={() => navigate('/karriere')} className="hover:text-[#1E40AF]">Karriere</button>
            </nav>

            <div className="flex items-center space-x-4">
              <button onClick={() => navigate('/company/login')} className="hidden md:block text-[#0F172A] font-semibold text-sm hover:text-[#1E40AF]">
                Logg inn
              </button>
              <button onClick={() => navigate('/company/register')} className="bg-[#1E40AF] text-white px-5 py-2 rounded-lg font-bold text-sm hover:bg-[#1E3A8A] transition-colors shadow-sm">
                Kom i gang
              </button>
            </div>
          </div>
        </header>

        {/* --- HERO SECTION --- */}
        <section className="relative pt-16 pb-20 lg:pt-28 lg:pb-32 bg-gradient-to-br from-[#EEF2FF] via-white to-white overflow-hidden">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20">
              {/* Left Column: Text */}
              <motion.div 
                initial={{ opacity: 0, x: -30 }} 
                animate={{ opacity: 1, x: 0 }} 
                transition={{ duration: 0.6 }}
                className="lg:w-1/2 text-center lg:text-left"
              >
                <div className="inline-flex items-center gap-2 bg-blue-50 border border-blue-100 text-blue-700 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wide mb-6">
                  <span className="w-2 h-2 bg-blue-600 rounded-full animate-pulse"></span> Sikker Varsling & HMS
                </div>
                <h1 className={`${styles.h1} mb-6`}>
                  Gjør arbeidsplassen <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#1E40AF] to-[#3B82F6]">trygg og lovlig</span>
                </h1>
                <p className={`${styles.body} mb-10 max-w-xl mx-auto lg:mx-0`}>
                  En komplett plattform for anonym varsling, avvikshåndtering og HMS. Tilpasset norske krav, enkelt for ansatte, trygt for ledelsen.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-12">
                  <button onClick={() => navigate('/company/register')} className={styles.buttonPrimary}>
                    Registrer Bedrift <SafeIcon icon={FiArrowRight} />
                  </button>
                  <button onClick={() => navigate('/book-demo')} className={styles.buttonSecondary}>
                    <SafeIcon icon={FiVideo} /> Se hvordan det virker
                  </button>
                </div>
                
                {/* TRUST BADGES */}
                <div className="flex flex-wrap justify-center lg:justify-start gap-x-6 gap-y-3 text-sm font-medium text-slate-600">
                  <div className="flex items-center gap-2">
                    <SafeIcon icon={FiAnchor} className="text-red-500 text-lg" /> <span>Norsk leverandør</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <SafeIcon icon={FiShield} className="text-green-500 text-lg" /> <span>GDPR-kompatibel</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <SafeIcon icon={FiAward} className="text-blue-500 text-lg" /> <span>EU Direktiv-tilpasset</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <SafeIcon icon={FiLock} className="text-teal-500 text-lg" /> <span>SSL-kryptert</span>
                  </div>
                </div>
              </motion.div>

              {/* Right Column: Dashboard UI Visualization */}
              <motion.div 
                initial={{ opacity: 0, x: 30 }} 
                animate={{ opacity: 1, x: 0 }} 
                transition={{ duration: 0.8, delay: 0.2 }}
                className="lg:w-1/2 w-full relative"
              >
                {/* Visual Placeholder for Dashboard */}
                <div className="relative bg-white rounded-3xl shadow-2xl border border-gray-100 p-6 transform rotate-1 hover:rotate-0 transition-transform duration-500 overflow-hidden">
                  {/* Sidebar Simulation */}
                  <div className="absolute left-0 top-0 bottom-0 w-16 bg-slate-50 border-r border-gray-100 flex flex-col items-center py-6 gap-6">
                    <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center text-white"><SafeIcon icon={FiShield} /></div>
                    <div className="w-8 h-8 rounded-lg bg-blue-100 text-blue-600 flex items-center justify-center"><SafeIcon icon={FiHome} /></div>
                    <div className="w-8 h-8 rounded-lg text-gray-400 flex items-center justify-center"><SafeIcon icon={FiFileText} /></div>
                    <div className="w-8 h-8 rounded-lg text-gray-400 flex items-center justify-center"><SafeIcon icon={FiUsers} /></div>
                    <div className="mt-auto w-8 h-8 rounded-lg text-gray-400 flex items-center justify-center"><SafeIcon icon={FiSettings} /></div>
                  </div>
                  
                  {/* Main Content Area */}
                  <div className="pl-16">
                    {/* Top Bar */}
                    <div className="flex items-center justify-between mb-8 border-b border-gray-50 pb-4">
                      <div className="flex items-center gap-3">
                        <span className="font-bold text-gray-700">Oversikt</span>
                      </div>
                      <div className="flex gap-2">
                        <div className="w-8 h-8 rounded-full bg-gray-50 flex items-center justify-center text-gray-400"><SafeIcon icon={FiSearch} size={14}/></div>
                        <div className="w-8 h-8 rounded-full bg-gray-50 flex items-center justify-center text-gray-400 relative">
                          <SafeIcon icon={FiBell} size={14}/>
                          <div className="absolute top-0 right-0 w-2 h-2 bg-red-500 rounded-full"></div>
                        </div>
                        <div className="w-8 h-8 rounded-full bg-purple-100 border border-purple-200 flex items-center justify-center text-purple-700 text-xs font-bold">AB</div>
                      </div>
                    </div>

                    {/* Dashboard Widgets */}
                    <div className="grid grid-cols-2 gap-4 mb-6">
                      <div className="bg-blue-50 p-4 rounded-2xl border border-blue-100">
                        <div className="flex justify-between items-start mb-2">
                          <div className="w-8 h-8 bg-white rounded-lg text-blue-600 flex items-center justify-center shadow-sm"><SafeIcon icon={FiActivity} /></div>
                          <span className="text-xs font-bold text-blue-600 bg-blue-100 px-2 py-0.5 rounded-full">+2</span>
                        </div>
                        <div className="text-2xl font-bold text-gray-800">4</div>
                        <div className="text-xs text-gray-500">Aktive varsler</div>
                      </div>
                      <div className="bg-orange-50 p-4 rounded-2xl border border-orange-100">
                        <div className="flex justify-between items-start mb-2">
                          <div className="w-8 h-8 bg-white rounded-lg text-orange-600 flex items-center justify-center shadow-sm"><SafeIcon icon={FiAlertTriangle} /></div>
                          <span className="text-xs font-bold text-orange-600 bg-orange-100 px-2 py-0.5 rounded-full">HMS</span>
                        </div>
                        <div className="text-2xl font-bold text-gray-800">12</div>
                        <div className="text-xs text-gray-500">RUH Siste 30 dager</div>
                      </div>
                    </div>

                    {/* Activity List */}
                    <div className="space-y-3">
                      <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Siste Hendelser</h4>
                      <div className="flex items-center gap-4 p-3 bg-white border border-gray-100 rounded-xl shadow-sm">
                        <div className="w-10 h-10 bg-green-50 rounded-lg flex items-center justify-center text-green-600"><SafeIcon icon={FiCheckCircle} /></div>
                        <div className="flex-1">
                          <div className="text-sm font-bold text-gray-800">Sak #2024-42 Lukket</div>
                          <div className="text-xs text-gray-500">Behandlet av HR (Anne)</div>
                        </div>
                        <span className="text-xs text-gray-400">10:42</span>
                      </div>
                      <div className="flex items-center gap-4 p-3 bg-white border border-gray-100 rounded-xl shadow-sm">
                        <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center text-blue-600"><SafeIcon icon={FiMessageSquare} /></div>
                        <div className="flex-1">
                          <div className="text-sm font-bold text-gray-800">Ny melding fra varsler</div>
                          <div className="text-xs text-gray-500">Sak #2024-45</div>
                        </div>
                        <span className="text-xs text-gray-400">09:15</span>
                      </div>
                    </div>
                  </div>

                  {/* Status Badge */}
                  <div className="absolute -bottom-6 -left-6 bg-white py-3 px-5 rounded-2xl shadow-xl border border-gray-100 flex items-center gap-3 animate-bounce-slow z-20">
                    <div className="bg-green-100 p-2 rounded-full text-green-600">
                      <SafeIcon icon={FiCheckCircle} className="text-2xl" />
                    </div>
                    <div>
                      <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">STATUS</p>
                      <p className="font-bold text-gray-900 text-sm">100% Compliant</p>
                    </div>
                  </div>
                </div>
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-[120%] bg-blue-100/30 rounded-full blur-3xl -z-10"></div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* --- SEKSJON 1: INTRO --- */}
        <ContentSection title="Hva er HMS varsling – og hvorfor er det viktig?" mode="authoritative">
          <p>HMS varsling handler om retten og muligheten til å si ifra om kritikkverdige forhold på arbeidsplassen. Dette kan være forhold som bryter med arbeidsmiljøloven, interne retningslinjer eller grunnleggende etiske normer. Typiske varsler kan gjelde trakassering, mobbing, diskriminering, brudd på HMS-regler, økonomiske misligheter eller andre alvorlige forhold.</p>
          <p>For arbeidsgiver er et godt varslingssystem langt mer enn et juridisk krav. Det er et viktig verktøy for å avdekke risiko tidlig, sikre et trygt arbeidsmiljø og bygge tillit mellom ansatte og ledelse. En profesjonell og strukturert varslingsløsning gjør det mulig å håndtere saker på en ryddig, dokumenterbar og rettssikker måte.</p>
          <div className="pt-4">
            <button onClick={() => navigate('/hms-varsling')} className="text-blue-600 font-bold hover:underline flex items-center gap-2">
              Les mer om HMS varsling <SafeIcon icon={FiArrowRight} />
            </button>
          </div>
        </ContentSection>

        {/* --- FEATURES SECTION --- */}
        <section id="features" className={`${styles.sectionPadding} bg-[#EEF2FF]`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-20">
              <h2 className={`${styles.h2} mb-6`}>Alt du trenger på ett sted</h2>
              <p className={`${styles.body} max-w-2xl mx-auto`}>
                Vi har samlet verktøyene som gjør det enkelt å håndtere varsling og HMS profesjonelt.
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {features.map((f, i) => (
                <motion.div 
                  key={i}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className={styles.cardContainer}
                >
                  <div className={`h-[90px] w-full flex items-center justify-center bg-${f.color}-50 border-b border-${f.color}-100/50`}>
                    <div className={`w-14 h-14 rounded-2xl flex items-center justify-center bg-white text-${f.color}-600 shadow-sm border border-${f.color}-100`}>
                      <SafeIcon icon={f.icon} className="text-2xl" />
                    </div>
                  </div>
                  <div className="p-8 bg-white flex-1 text-center">
                    <h3 className={`${styles.h3} mb-3`}>{f.title}</h3>
                    <p className={`${styles.body} text-sm leading-relaxed`}>{f.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* --- SEKSJON 2: PLATFORM --- */}
        <ContentSection title="Én helhetlig varslingsplattform – ikke bare et skjema" mode="authoritative">
          <div className="md:flex md:gap-12 items-center">
            <div className="md:w-3/5 space-y-6">
              <p>HMS Varsling er utviklet som en komplett digital varslingsplattform, der hele prosessen fra innmelding til avsluttet sak henger sammen. Varsler registreres trygt, anonymt dersom ønskelig, og behandles strukturert i et system som ivaretar både varslerens og virksomhetens rettigheter.</p>
              <p>Plattformen støtter hele livsløpet i en varslingssak: mottak, vurdering, dialog, dokumentasjon og oppfølging. Dette gir virksomheten full oversikt og nødvendig sporbarhet, samtidig som personvern og GDPR ivaretas.</p>
              <div className="pt-2">
                <button onClick={() => navigate('/hvordan-fungerer-det')} className="text-blue-600 font-bold hover:underline flex items-center gap-2">
                  Se hvordan prosessen fungerer <SafeIcon icon={FiArrowRight} />
                </button>
              </div>
            </div>
            {/* Process Visualization */}
            <div className="hidden md:block md:w-2/5 p-6 bg-blue-50/50 rounded-2xl border border-blue-100">
              <h3 className="text-sm font-bold text-blue-800 uppercase tracking-wider mb-4 border-b border-blue-200 pb-2">Saksgang i praksis</h3>
              <div className="space-y-4">
                {/* Step 1 */}
                <div className="flex gap-4 items-start">
                  <div className="flex flex-col items-center">
                    <div className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center z-10 shadow-sm"><SafeIcon icon={FiSend} size={14} /></div>
                    <div className="h-8 w-0.5 bg-blue-200"></div>
                  </div>
                  <div className="bg-white p-3 rounded-lg shadow-sm w-full border border-blue-50">
                    <div className="text-sm font-bold text-gray-800">Varsel Mottatt</div>
                    <div className="text-xs text-gray-500">Automatisk kvittering sendt</div>
                  </div>
                </div>
                {/* Step 2 */}
                <div className="flex gap-4 items-start">
                  <div className="flex flex-col items-center">
                    <div className="w-8 h-8 rounded-full bg-indigo-500 text-white flex items-center justify-center z-10 shadow-sm"><SafeIcon icon={FiSearch} size={14} /></div>
                    <div className="h-8 w-0.5 bg-blue-200"></div>
                  </div>
                  <div className="bg-white p-3 rounded-lg shadow-sm w-full border border-blue-50">
                    <div className="text-sm font-bold text-gray-800">Vurdering</div>
                    <div className="text-xs text-gray-500">Habilitetsvurdering og klassifisering</div>
                  </div>
                </div>
                {/* Step 3 */}
                <div className="flex gap-4 items-start">
                  <div className="flex flex-col items-center">
                    <div className="w-8 h-8 rounded-full bg-green-500 text-white flex items-center justify-center z-10 shadow-sm"><SafeIcon icon={FiCheckCircle} size={14} /></div>
                  </div>
                  <div className="bg-white p-3 rounded-lg shadow-sm w-full border border-blue-50">
                    <div className="text-sm font-bold text-gray-800">Konklusjon & Tiltak</div>
                    <div className="text-xs text-gray-500">Dokumentert oppfølging</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </ContentSection>

        {/* --- MODULES SECTION --- */}
        <section id="modules" className={`${styles.sectionPadding} bg-white`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col md:flex-row items-end justify-between mb-16 gap-6">
              <div className="max-w-2xl">
                <h2 className={`${styles.h2} mb-4`}>Våre moduler</h2>
                <p className={styles.body}>Skreddersydde portaler for alle involverte parter sikrer en ryddig prosess.</p>
              </div>
              <button onClick={() => navigate('/company/login')} className="text-[#1E40AF] font-bold flex items-center gap-2 hover:gap-3 transition-all">
                Gå til oversikt <SafeIcon icon={FiArrowRight} />
              </button>
            </div>
            
            <div className="flex flex-wrap justify-center gap-6">
              {modules.map((m, i) => (
                <motion.div 
                  key={i} 
                  className={`${styles.cardContainer} w-full md:w-[calc(50%-12px)] lg:w-[calc(33.333%-16px)] max-w-[400px]`}
                  onClick={() => navigate(m.path)}
                  whileHover={{ y: -5 }}
                >
                  <div className={`${m.headerBg} h-[120px] flex items-center justify-center relative overflow-hidden`}>
                    <div className="absolute -right-4 -top-4 w-24 h-24 bg-white/20 rounded-full blur-xl"></div>
                    <div className={`w-16 h-16 rounded-2xl bg-white flex items-center justify-center shadow-lg ${m.iconColor} relative z-10`}>
                      <SafeIcon icon={m.icon} className="text-3xl" />
                    </div>
                    {m.badge && (
                      <div className="absolute top-4 right-4 bg-red-500 text-white text-[10px] font-bold uppercase px-2 py-1 rounded-full shadow-sm">
                        {m.badge}
                      </div>
                    )}
                  </div>
                  <div className={`${m.bodyBg} p-8 flex-1 flex flex-col items-center text-center`}>
                    <h3 className={`${styles.h3} mb-3`}>{m.title}</h3>
                    <p className="text-[#475569] text-sm mb-8 leading-relaxed flex-grow">{m.description}</p>
                    <span className="text-[#1E40AF] font-bold text-sm bg-white px-4 py-2 rounded-lg shadow-sm border border-blue-50 flex items-center gap-2 group-hover:bg-blue-600 group-hover:text-white transition-all">
                      Åpne modul
                    </span>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* --- SEKSJON 3 & 4 --- */}
        <ContentSection title="Fleksible moduler tilpasset ulike behov" className="bg-[#F8FAFC]" mode="authoritative">
          <p>Behovet for varsling og HMS-oppfølging varierer fra virksomhet til virksomhet. Noen trenger kun en trygg og anonym varslingskanal, mens andre har behov for ekstern håndtering, uavhengige utredninger eller utvidet dokumentasjon og oppfølging.</p>
          <p>HMS Varsling er bygget modulbasert slik at virksomheten kan velge det omfanget som er riktig – uten unødvendig kompleksitet.</p>
        </ContentSection>

        <ContentSection title="Hva krever lovverket av arbeidsgiver?" mode="authoritative">
          <p>I henhold til arbeidsmiljøloven har arbeidsgivere plikt til å legge til rette for varsling og sikre forsvarlig behandling av varslingssaker. Virksomheter med mer enn fem ansatte skal ha skriftlige rutiner for varsling.</p>
          <p>Et profesjonelt varslingssystem skal blant annet sikre:</p>
          <ul className="list-disc pl-5 space-y-3 text-slate-600">
            <li>Mulighet for anonym varsling</li>
            <li>Beskyttelse av varslerens identitet</li>
            <li>Rett til kontradiksjon</li>
            <li>Dokumentert saksbehandling</li>
            <li>Overholdelse av GDPR og personvernlovgivning</li>
          </ul>
          <div className="pt-4">
            <button onClick={() => navigate('/arbeidsmiljoloven-varsling')} className="text-blue-600 font-bold hover:underline flex items-center gap-2">
              Les om dine plikter i Arbeidsmiljøloven <SafeIcon icon={FiArrowRight} />
            </button>
          </div>
        </ContentSection>

        {/* --- PRICING SECTION --- */}
        <section id="pricing" className={`${styles.sectionPadding} bg-[#EEF2FF]`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-20">
              <h2 className={`${styles.h2} mb-6`}>Enkel og forutsigbar pris</h2>
              <p className={`${styles.body} max-w-2xl mx-auto`}>Ingen skjulte kostnader. Velg pakken som passer din bedrift.</p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {topRowPlans.map((id) => {
                const plan = getPlan(id);
                if (!plan) return null;
                const isPopular = plan.popular;
                
                return (
                  <div 
                    key={id} 
                    className={`${styles.cardContainer} ${isPopular ? 'ring-4 ring-[#1E40AF]/10 transform scale-[1.02] z-10 shadow-2xl' : 'shadow-lg'}`}
                  >
                    <div className={`p-8 text-center ${isPopular ? 'bg-[#1E40AF] text-white' : 'bg-white text-[#0F172A]'} border-b border-gray-100`}>
                      {isPopular && (
                        <div className="bg-white/20 text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider inline-block mb-4 backdrop-blur-sm">
                          Mest Valgt
                        </div>
                      )}
                      <h3 className={`text-[24px] font-bold mb-2`}>{plan.name}</h3>
                      <p className={`text-sm ${isPopular ? 'text-blue-100' : 'text-[#64748B]'}`}>{plan.employees}</p>
                      <div className="mt-6 flex items-baseline justify-center">
                        <span className="text-[48px] font-bold tracking-tight">{plan.price}</span>
                        <span className={`text-sm font-medium ml-2 ${isPopular ? 'text-blue-200' : 'text-gray-400'}`}>kr / mnd</span>
                      </div>
                    </div>
                    <div className="bg-[#F8FAFF] p-8 flex-1 flex flex-col">
                      <ul className="space-y-4 mb-8 flex-1">
                        {plan.features.slice(0, 5).map((f, i) => (
                          <li key={i} className="flex items-start gap-3 text-[15px] text-[#475569]">
                            <div className="mt-1 w-5 h-5 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                              <SafeIcon icon={FiCheck} className="text-green-600 text-xs" />
                            </div>
                            <span>{f}</span>
                          </li>
                        ))}
                      </ul>
                      <button 
                        onClick={() => navigate('/company/register', { state: { selectedPlan: plan.id } })}
                        className={`${isPopular ? 'bg-[#1E40AF] text-white hover:bg-[#1B3A9A]' : 'bg-white border border-gray-200 text-[#0F172A] hover:bg-gray-50'} w-full py-4 rounded-xl font-bold shadow-sm transition-all`}
                      >
                        {plan.buttonText || 'Velg denne'}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
            
            <div className="text-center mt-12 text-[#64748B] text-sm">
              <p>Engangsoppsett: 4 990 kr eks. mva.</p>
            </div>
          </div>
        </section>

        {/* --- PUBLIC & VOLUNTEER SECTION --- */}
        <section className={`${styles.sectionPadding} bg-[#E6F4F1]`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className={`${styles.h2} mb-4 text-[#0F2A44]`}>Spesialtilpassede løsninger</h2>
              <p className={`${styles.body} max-w-2xl mx-auto`}>
                Vi har egne pakker for offentlig sektor og frivillighet.
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              {bottomRowPlans.map((id) => {
                const plan = getPlan(id);
                if (!plan) return null;
                
                return (
                  <div key={id} className={styles.cardContainer}>
                    <div className="p-8 bg-white border-b border-gray-100 text-center">
                      <h3 className={`${styles.h3} mb-2`}>{plan.name}</h3>
                      <p className="text-sm text-[#475569] mb-4">{plan.employees}</p>
                      <div className="text-[32px] font-bold text-[#0F172A]">{plan.price} <span className="text-sm text-gray-400 font-normal">kr/mnd</span></div>
                    </div>
                    <div className="p-8 bg-[#FAFCFF] flex-1 flex flex-col">
                      <ul className="space-y-3 mb-8 flex-1">
                        {plan.features.slice(0, 4).map((f, i) => (
                          <li key={i} className="flex items-start gap-3 text-[15px] text-[#475569]">
                            <SafeIcon icon={FiCheckCircle} className="text-[#2FA4A9] mt-1 flex-shrink-0" />
                            <span>{f}</span>
                          </li>
                        ))}
                      </ul>
                      <button 
                        onClick={() => navigate('/company/register', { state: { selectedPlan: plan.id } })}
                        className={styles.buttonSecondary + " w-full"}
                      >
                        Kom i gang
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* --- SEKSJON 5: EXTERNAL HANDLING --- */}
        <ContentSection title="Når er ekstern og uavhengig varsling nødvendig?" className="bg-white" mode="reflective">
          <p>I enkelte saker kan det være avgjørende at varslingen håndteres av en ekstern og uavhengig part. Dette gjelder særlig der det foreligger interessekonflikter, mistanke om ledelsesinvolvering eller lav tillit til intern håndtering.</p>
          <p>Ekstern varsling bidrar til økt trygghet, objektivitet og legitimitet i prosessen for alle parter.</p>
          <div className="pt-4">
            <button onClick={() => navigate('/ekstern-varsling')} className="text-blue-600 font-bold hover:underline flex items-center gap-2">
              Les mer om ekstern varsling <SafeIcon icon={FiArrowRight} />
            </button>
          </div>
        </ContentSection>

        {/* --- EXTERNAL SERVICES SECTION (Dark) --- */}
        <section className={`${styles.sectionPadding} bg-[#0F2A44] relative overflow-hidden`}>
          <div className="absolute inset-0 opacity-10 bg-[radial-gradient(#ffffff_1px,transparent_1px)] [background-size:20px_20px]"></div>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="flex flex-col lg:flex-row gap-16 items-center">
              <div className="lg:w-1/2 text-white">
                <div className="inline-block bg-white/10 px-4 py-1 rounded-full text-sm font-semibold mb-6 border border-white/20">Ekstratjenester</div>
                <h2 className="text-4xl md:text-5xl font-bold mb-6 leading-tight">Trenger dere en uavhengig part?</h2>
                <p className="text-blue-100 text-lg leading-relaxed mb-8">
                  For vanskelige saker eller når habilitet er et tema, tilbyr vi ekstern saksbehandling. Våre eksperter håndterer saken profesjonelt og nøytralt.
                </p>
                <div className="flex gap-4">
                  <div className="flex items-center gap-3 bg-white/5 p-4 rounded-xl border border-white/10">
                    <SafeIcon icon={FiShield} className="text-2xl text-blue-300" />
                    <div>
                      <div className="font-bold">100% Nøytralt</div>
                      <div className="text-xs text-blue-200">Ingen bindinger</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 bg-white/5 p-4 rounded-xl border border-white/10">
                    <SafeIcon icon={FiLock} className="text-2xl text-blue-300" />
                    <div>
                      <div className="font-bold">Juridisk Trygt</div>
                      <div className="text-xs text-blue-200">Følger lovverket</div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="lg:w-1/2 grid gap-6">
                {/* Service Cards */}
                {displayServices.map((service, idx) => (
                  <div key={idx} className="bg-white rounded-2xl p-6 shadow-xl flex items-center gap-6 hover:translate-x-2 transition-transform cursor-pointer" onClick={() => handleContactClick(service.name)}>
                    <div className={`w-16 h-16 ${service.id === 'external-partner' ? 'bg-[#ECFDF5] text-[#059669]' : 'bg-[#EEF2FF] text-[#1E40AF]'} rounded-xl flex items-center justify-center flex-shrink-0`}>
                      <SafeIcon icon={service.icon === 'FiBriefcase' ? FiBriefcase : FiShield} className="text-3xl" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold text-lg text-[#0F172A]">{service.name}</h3>
                      <p className="text-sm text-gray-500 mb-2">{service.description}</p>
                      <div className={`font-bold ${service.id === 'external-partner' ? 'text-[#059669]' : 'text-[#1E40AF]'}`}>
                        {service.price} kr <span className="text-gray-400 font-normal text-xs">/ {service.timeframe}</span>
                      </div>
                    </div>
                    <SafeIcon icon={FiArrowRight} className="text-gray-300" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* --- FAQ SECTION --- */}
        <section id="faq" className={`${styles.sectionPadding} bg-white`}>
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className={`${styles.h2} mb-4`}>Ofte stilte spørsmål</h2>
              <p className={`${styles.body}`}>Her finner du svar på det meste om lovverk, direktiver og tjenesten.</p>
            </div>
            
            <div className="space-y-4">
              {faqItems.map((item, index) => {
                const isOpen = activeAccordion === index;
                return (
                  <div key={index} className={`border rounded-2xl overflow-hidden transition-all ${isOpen ? 'border-blue-200 shadow-md' : 'border-gray-100'}`}>
                    <button 
                      onClick={() => toggleAccordion(index)}
                      className={`w-full flex justify-between items-center py-5 px-6 text-left font-bold text-[#0F172A] hover:bg-gray-50 transition-colors ${isOpen ? 'bg-[#F4F7FB]' : 'bg-white'}`}
                    >
                      <span className="text-[17px]">{item.question}</span>
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${isOpen ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-500'}`}>
                        <SafeIcon icon={isOpen ? FiMinus : FiPlus} />
                      </div>
                    </button>
                    <AnimatePresence>
                      {isOpen && (
                        <motion.div 
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          className="bg-[#F4F7FB]"
                        >
                          <div className="pb-8 px-6 text-[16px] text-[#475569] leading-relaxed max-w-2xl">
                            {item.answer}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* --- SEKSJON 6: REPUTATION --- */}
        <ContentSection title="Trygg varsling styrker arbeidsmiljø og omdømme" className="bg-[#F8FAFC]" mode="reflective">
          <p>Et velfungerende varslingssystem er en viktig del av virksomhetens HMS-arbeid og internkontroll. Når ansatte opplever at det er trygt å si ifra, øker sannsynligheten for at kritikkverdige forhold avdekkes tidlig.</p>
          <p>HMS Varsling bidrar til å skape åpenhet, tillit og forutsigbarhet i håndteringen av varsler.</p>
        </ContentSection>

        {/* --- CTA SECTION (UPDATED TEXT) --- */}
        <section className="bg-white pb-24 px-4">
          <div className="max-w-5xl mx-auto">
            <div className="bg-[#1E40AF] rounded-[32px] p-10 md:p-20 text-center text-white relative overflow-hidden shadow-2xl">
              {/* Decorative circles */}
              <div className="absolute top-0 right-0 w-96 h-96 bg-white/10 rounded-full -mr-32 -mt-32 blur-3xl"></div>
              <div className="absolute bottom-0 left-0 w-64 h-64 bg-white/5 rounded-full -ml-20 -mb-20 blur-2xl"></div>
              
              <div className="relative z-10">
                <h2 className="text-3xl md:text-5xl font-bold mb-6 text-white tracking-tight">Klar for en tryggere arbeidsplass?</h2>
                <p className="text-blue-100 text-lg md:text-xl mb-10 max-w-2xl mx-auto font-medium">
                  Det tar under 2 minutter å sette opp deres egen varslingskanal. Ingen teknisk kompetanse kreves.
                </p>
                <div className="flex flex-col sm:flex-row justify-center gap-4">
                  <button onClick={() => navigate('/company/register')} className="bg-white text-[#1E40AF] px-10 py-4 rounded-xl font-bold text-lg hover:bg-blue-50 transition-colors shadow-lg transform hover:-translate-y-1 duration-200">
                    Registrer Bedrift
                  </button>
                  <button onClick={() => navigate('/book-demo')} className="bg-[#1B3A9A] text-white border border-white/20 px-8 py-4 rounded-xl font-bold text-lg hover:bg-[#152e7a] transition-colors">
                    Book Demo
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* --- SEKSJON 7: SEO FOOTER & COMPLIANCE BAR --- */}
        <ContentSection title="Digital plattform for HMS varsling og saksbehandling" className="bg-slate-50 border-t border-slate-200" mode="authoritative">
          <p>
            HMS Varsling er en norskutviklet digital plattform for HMS varsling, anonym varsling og saksbehandling av varslingssaker. Løsningen er utviklet for å møte kravene i norsk og europeisk regelverk, inkludert arbeidsmiljøloven og GDPR.
          </p>
          <p>
            Plattformen gir arbeidsgivere et strukturert og dokumenterbart system for håndtering av varsler, samtidig som varslerens rettigheter og personvern ivaretas.
          </p>
        </ContentSection>

        {/* --- COMPLIANCE PROOF BAR (NEW) --- */}
        <ComplianceBar />

        {/* --- FOOTER --- */}
        <footer className="bg-[#0F172A] text-slate-300 py-24 border-t border-slate-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-12 lg:gap-16 mb-16">
              {/* Column 1 */}
              <div className="col-span-1 md:col-span-1">
                <div className="flex items-center gap-3 mb-6 text-white">
                  <div className="bg-[#1E40AF] p-2 rounded-xl">
                    <SafeIcon icon={FiShield} className="text-2xl" />
                  </div>
                  <span className="font-bold text-2xl tracking-tight">HMSVarsling</span>
                </div>
                <p className="text-sm leading-relaxed mb-8 text-slate-400">
                  Norges ledende plattform for trygg varsling og HMS-rapportering. Vi hjelper bedrifter med å skape trygge arbeidsplasser gjennom teknologi og fagkunnskap.
                </p>
                <div className="flex gap-3 mb-6">
                  <a href="mailto:salg@hmsvarsling.no" className="w-10 h-10 flex items-center justify-center bg-slate-800 text-white rounded-xl hover:bg-[#1E40AF] transition-colors"><SafeIcon icon={FiMail} /></a>
                  <a href="tel:+4740551989" className="w-10 h-10 flex items-center justify-center bg-slate-800 text-white rounded-xl hover:bg-[#1E40AF] transition-colors"><SafeIcon icon={FiPhone} /></a>
                </div>
                <div className="text-sm text-slate-400 space-y-2">
                  <p>E-post: <a href="mailto:salg@hmsvarsling.no" className="text-slate-300 hover:text-white transition-colors font-medium">salg@hmsvarsling.no</a></p>
                  <p>Tlf: <a href="tel:+4740551989" className="text-slate-300 hover:text-white transition-colors font-medium">40 55 19 89</a></p>
                </div>
              </div>

              {/* Column 2 */}
              <div>
                <h4 className="text-white font-bold mb-6 text-base">Løsningen</h4>
                <ul className="space-y-4 text-sm font-medium">
                  <li><button onClick={() => navigate('/hms-varsling')} className="hover:text-white transition-colors text-left">Hva er HMS varsling?</button></li>
                  <li><button onClick={() => navigate('/anonym-varsling')} className="hover:text-white transition-colors text-left">Anonym varsling</button></li>
                  <li><button onClick={() => navigate('/hvordan-fungerer-det')} className="hover:text-white transition-colors text-left">Hvordan fungerer det?</button></li>
                  <li><button onClick={() => scrollToSection('features')} className="hover:text-white transition-colors text-left">Funksjoner</button></li>
                  <li><button onClick={() => scrollToSection('pricing')} className="hover:text-white transition-colors text-left">Priser</button></li>
                </ul>
              </div>

              {/* Column 3 */}
              <div>
                <h4 className="text-white font-bold mb-6 text-base">Ressurser</h4>
                <ul className="space-y-4 text-sm font-medium">
                  <li><button onClick={() => navigate('/arbeidsmiljoloven-varsling')} className="hover:text-white transition-colors text-left">Lovverk</button></li>
                  <li><button onClick={() => navigate('/ekstern-varsling')} className="hover:text-white transition-colors text-left">Ekstern varsling</button></li>
                  <li><button onClick={() => navigate('/regelverk-og-etterlevelse')} className="hover:text-white transition-colors text-left">Compliance</button></li>
                  <li><button onClick={() => navigate('/utreder/register')} className="hover:text-white transition-colors text-blue-300 text-left">Bli Utreder</button></li>
                  <li><button onClick={() => navigate('/partner/register')} className="hover:text-white transition-colors text-purple-300 text-left">Bli Partner</button></li>
                  <li><button onClick={() => navigate('/karriere')} className="hover:text-white transition-colors text-left">Karriere</button></li>
                  <li><button onClick={() => navigate('/support')} className="hover:text-white transition-colors text-left">Kundesupport</button></li>
                </ul>
              </div>

              {/* Column 4: PORTALER */}
              <div>
                <h4 className="text-white font-bold mb-6 text-base">Portaler</h4>
                <ul className="space-y-4 text-sm font-medium">
                  <li><button onClick={() => navigate('/utreder/login')} className="hover:text-white transition-colors text-left">Utreder portal login</button></li>
                  <li><button onClick={() => navigate('/partner/login')} className="hover:text-white transition-colors text-left">Partner portal login</button></li>
                  <li><button onClick={() => navigate('/seller/login')} className="hover:text-white transition-colors text-left">Selger portal login</button></li>
                  <li><button onClick={() => navigate('/superadmin/login')} className="hover:text-white transition-colors text-left">Intern portal Login</button></li>
                </ul>
              </div>
            </div>

            <div className="border-t border-slate-800 pt-8 flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-slate-500 font-medium">
              <p>© {new Date().getFullYear()} HMSVarsling. Alle rettigheter reservert.</p>
              <div className="flex gap-4">
                <button onClick={() => navigate('/privacy')} className="hover:text-white transition-colors">Personvern</button>
                <button onClick={() => navigate('/terms')} className="hover:text-white transition-colors">Vilkår</button>
                <button onClick={() => navigate('/cookies')} className="hover:text-white transition-colors">Cookies</button>
              </div>
            </div>
          </div>
        </footer>

        {/* --- FLOATING BUTTON --- */}
        <AnimatePresence>
          {showFloatingBtn && (
            <motion.button
              initial={{ opacity: 0, y: 20, scale: 0.9 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.9 }}
              onClick={() => navigate('/book-demo')}
              className="fixed bottom-8 right-8 bg-[#1E40AF] text-white px-6 py-4 rounded-full shadow-2xl hover:bg-[#1B3A9A] transition-all z-50 flex items-center gap-3 font-bold text-sm border-4 border-white"
            >
              <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
              Book Demo
            </motion.button>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default EnhancedLandingPage;